import React, { useState, useEffect, useCallback } from 'react';
import {
  LayoutGrid, Palette, MessageCircle, Sparkles, Users, Clock,
  User, ShieldCheck, Award, Globe, ArrowRight, X, ArrowUpRight, ChevronDown
} from 'lucide-react';
import useInView from '../hooks/useInView';
import { coreFeatures, findFeatureById } from '../data/coreFeatures';

const ICONS = { LayoutGrid, Palette, MessageCircle, Sparkles, Users, Clock, User, ShieldCheck, Award, Globe };

const FadeIn = ({ children, delay = 0, className = '' }) => {
  const [ref, inView] = useInView(0.05);
  return (
    <div ref={ref} className={`transition-all duration-700 ${inView ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-6'} ${className}`} style={{ transitionDelay: `${delay}ms` }}>
      {children}
    </div>
  );
};

const FeatureCard = ({ feature, onOpen, index }) => {
  const Icon = ICONS[feature.icon] || LayoutGrid;
  return (
    <button
      data-testid={`feature-card-${feature.id}`}
      onClick={() => onOpen(feature.id)}
      className="group relative text-left h-full w-full rounded-2xl overflow-hidden focus:outline-none focus-visible:ring-2 focus-visible:ring-purple-400/60"
    >
      {/* Animated neon gradient border */}
      <div
        className="absolute inset-0 rounded-2xl opacity-60 group-hover:opacity-100 transition-opacity duration-500"
        style={{
          background: 'linear-gradient(135deg, rgba(157,76,221,0.55), rgba(0,240,255,0.35), rgba(255,0,127,0.4))',
          padding: '1px',
          WebkitMask: 'linear-gradient(#fff 0 0) content-box, linear-gradient(#fff 0 0)',
          WebkitMaskComposite: 'xor',
          maskComposite: 'exclude',
        }}
      />

      {/* Card body */}
      <div className="relative h-full rounded-2xl p-6 bg-[#0A0816]/85 backdrop-blur-xl border border-white/[0.06] transition-all duration-500 group-hover:-translate-y-1 group-hover:bg-[#0D0B22]/90 group-hover:shadow-[0_18px_60px_-20px_rgba(157,76,221,0.45)]">
        {/* Soft accent glow on hover */}
        <div
          className={`absolute -top-8 -right-8 w-32 h-32 rounded-full blur-3xl opacity-0 group-hover:opacity-60 transition-opacity duration-700 bg-gradient-to-br ${feature.color}`}
        />

        <div className="relative flex items-start gap-4 mb-4">
          <div className={`w-11 h-11 rounded-2xl bg-gradient-to-br ${feature.color} flex items-center justify-center shadow-lg shadow-black/40 transition-transform duration-500 group-hover:scale-110 group-hover:rotate-3`}>
            <Icon size={18} className="text-white" />
          </div>
          <span className="ml-auto text-[10px] font-outfit font-semibold text-white/30 tracking-[0.18em] uppercase">
            {String(index + 1).padStart(2, '0')}
          </span>
        </div>

        <h3 className="relative font-outfit font-bold text-white text-[17px] sm:text-base leading-snug mb-2.5 tracking-tight">{feature.title}</h3>
        <p className="relative text-white/50 text-[13px] leading-relaxed mb-5">{feature.short}</p>

        <div className="relative flex items-center justify-between">
          <span className="inline-flex items-center gap-1.5 text-[11px] font-semibold text-purple-300 tracking-wide transition-all duration-300 group-hover:gap-2.5 group-hover:text-purple-200">
            Explore feature
            <ArrowUpRight size={13} className="transition-transform duration-300 group-hover:rotate-12" />
          </span>
          <span className="hidden sm:inline-flex items-center gap-1 text-[10px] text-white/30 group-hover:text-white/55 transition-colors">
            <span className="w-1 h-1 rounded-full bg-emerald-400/70" /> Tap to expand
          </span>
        </div>
      </div>
    </button>
  );
};

const DeepDivePanel = ({ feature, onClose, onJump }) => {
  const Icon = ICONS[feature.icon] || LayoutGrid;
  return (
    <div data-testid={`feature-modal-${feature.id}`} className="fixed inset-0 z-[100] flex items-stretch sm:items-center justify-center px-0 sm:px-4 py-0 sm:py-8">
      {/* Backdrop */}
      <button
        data-testid="feature-modal-backdrop"
        onClick={onClose}
        aria-label="Close"
        className="absolute inset-0 bg-black/75 backdrop-blur-md animate-[fadeIn_.25s_ease-out_forwards]"
        style={{ animation: 'fadeIn .25s ease-out forwards' }}
      />

      {/* Panel */}
      <div
        className="relative w-full sm:max-w-3xl max-h-[100dvh] sm:max-h-[90vh] overflow-y-auto rounded-none sm:rounded-3xl bg-[#0A0816]/95 border border-white/10 shadow-[0_30px_120px_-20px_rgba(0,0,0,0.85)]"
        style={{ animation: 'panelIn .35s cubic-bezier(.16,1,.3,1) forwards' }}
      >
        {/* Gradient header band */}
        <div className={`h-1.5 w-full bg-gradient-to-r ${feature.color}`} />

        <div className="sticky top-0 z-10 flex items-center justify-between px-5 sm:px-8 py-4 bg-[#0A0816]/95 backdrop-blur-xl border-b border-white/8">
          <div className="flex items-center gap-3">
            <div className={`w-10 h-10 rounded-2xl bg-gradient-to-br ${feature.color} flex items-center justify-center shadow-lg`}>
              <Icon size={16} className="text-white" />
            </div>
            <div>
              <p className="text-[10px] font-outfit font-semibold text-purple-300 tracking-[0.18em] uppercase">Feature deep dive</p>
              <h3 className="font-outfit font-bold text-white text-base sm:text-lg leading-tight">{feature.title}</h3>
            </div>
          </div>
          <button
            data-testid="feature-modal-close"
            onClick={onClose}
            aria-label="Close panel"
            className="w-9 h-9 rounded-full bg-white/5 border border-white/10 flex items-center justify-center text-white/60 hover:text-white hover:bg-white/10 hover:border-white/20 transition-all"
          >
            <X size={15} />
          </button>
        </div>

        <div className="px-5 sm:px-8 py-7 space-y-7">
          <p className="text-white/55 text-sm leading-relaxed">{feature.short}</p>

          <Block label="What it is" body={feature.deepDive.whatItIs} tone="violet" />
          <Block label="Why it matters for children" body={feature.deepDive.whyKids} tone="fuchsia" />
          <Block label="Why parents can trust it" body={feature.deepDive.whyParents} tone="cyan" />
          <Block label="How it fits into the ROMIND KIDS ecosystem" body={feature.deepDive.ecosystem} tone="emerald" />

          {/* Feature Report */}
          <div data-testid={`feature-report-${feature.id}`} className="rounded-2xl border border-white/8 bg-white/[0.03] p-5 sm:p-6">
            <div className="flex items-center gap-2 mb-4">
              <div className="w-1.5 h-1.5 rounded-full bg-purple-400" />
              <p className="text-[10px] font-outfit font-semibold text-purple-300 tracking-[0.2em] uppercase">Feature Report</p>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-6 gap-y-4">
              <ReportLine label="Purpose" value={feature.report.purpose} />
              <ReportLine label="Child benefit" value={feature.report.childBenefit} />
              <ReportLine label="Parent trust benefit" value={feature.report.parentBenefit} />
              <ReportLine label="Safety note" value={feature.report.safetyNote} />
            </div>
            <div className="mt-5">
              <p className="text-[10px] font-outfit font-semibold text-white/40 tracking-[0.18em] uppercase mb-2">Connected features</p>
              <div className="flex flex-wrap gap-2">
                {feature.report.connectedTo.map((name) => {
                  const target = coreFeatures.find((f) => f.title === name);
                  return (
                    <button
                      key={name}
                      onClick={() => target && onJump(target.id)}
                      data-testid={`connected-${target ? target.id : 'unknown'}`}
                      className="text-[11px] px-3 py-1.5 rounded-full bg-white/5 border border-white/10 text-white/70 hover:text-white hover:border-purple-400/40 hover:bg-purple-500/10 transition-all inline-flex items-center gap-1.5"
                    >
                      {name}
                      <ArrowRight size={10} />
                    </button>
                  );
                })}
              </div>
            </div>
          </div>

          {/* Concept Preview / Real render */}
          <div className="rounded-2xl border border-dashed border-white/12 bg-white/[0.02] p-4 sm:p-6">
            <div className="flex items-center justify-between mb-3">
              <p className="text-[10px] font-outfit font-semibold text-white/40 tracking-[0.18em] uppercase">
                {feature.previewImage ? 'Visual · Real Render' : 'Visual · Concept Preview'}
              </p>
              <span className="text-[10px] text-white/30">{feature.previewImage ? 'In development' : 'In development'}</span>
            </div>
            {feature.previewImage ? (
              <div className="relative mx-auto w-full max-w-[260px]">
                <div className={`absolute -inset-4 rounded-[48px] bg-gradient-to-br ${feature.color} opacity-30 blur-[40px] pointer-events-none`} />
                <img
                  src={feature.previewImage}
                  alt={`ROMIND KIDS — ${feature.title}`}
                  className="relative w-full h-auto select-none drop-shadow-[0_14px_40px_rgba(0,0,0,0.55)]"
                  draggable="false"
                  loading="lazy"
                />
              </div>
            ) : (
              <div className={`h-32 sm:h-40 rounded-xl bg-gradient-to-br ${feature.color} opacity-30 relative overflow-hidden`}>
                <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_top,rgba(255,255,255,0.18),transparent_60%)]" />
                <div className="absolute bottom-3 left-4 right-4 flex items-center justify-between">
                  <span className="text-[10px] text-white/70 font-outfit font-semibold tracking-widest uppercase">Concept</span>
                  <span className="text-[10px] text-white/50">Not a final UI</span>
                </div>
              </div>
            )}
            <p className="text-white/35 text-[11px] mt-3 leading-relaxed">
              {feature.previewImage
                ? 'Render captured from active product development. Final UI may continue to evolve before launch.'
                : 'Real interface screenshots will replace this concept preview as the platform approaches launch. Final visuals may differ.'}
            </p>
          </div>
        </div>

        <div className="sticky bottom-0 px-5 sm:px-8 py-4 bg-[#0A0816]/95 backdrop-blur-xl border-t border-white/8 flex flex-col sm:flex-row gap-3 sm:items-center sm:justify-between">
          <span className="text-[11px] text-white/40">Two coexisting layers, one platform — built for kids, trusted by parents.</span>
          <button
            data-testid="feature-modal-close-bottom"
            onClick={onClose}
            className="inline-flex items-center justify-center gap-1.5 px-5 py-2 rounded-full text-xs font-semibold bg-purple-600/80 border border-purple-400/40 text-white hover:bg-purple-500 transition-all"
          >
            Close
          </button>
        </div>
      </div>

      <style>{`
        @keyframes fadeIn { from { opacity: 0 } to { opacity: 1 } }
        @keyframes panelIn { from { opacity: 0; transform: translateY(24px) scale(0.985) } to { opacity: 1; transform: translateY(0) scale(1) } }
      `}</style>
    </div>
  );
};

const TONE = {
  violet: { dot: 'bg-violet-400', label: 'text-violet-300' },
  fuchsia: { dot: 'bg-fuchsia-400', label: 'text-fuchsia-300' },
  cyan: { dot: 'bg-cyan-400', label: 'text-cyan-300' },
  emerald: { dot: 'bg-emerald-400', label: 'text-emerald-300' },
};

const Block = ({ label, body, tone = 'violet' }) => (
  <div>
    <div className="flex items-center gap-2 mb-2">
      <span className={`w-1.5 h-1.5 rounded-full ${TONE[tone].dot}`} />
      <p className={`text-[10px] font-outfit font-semibold ${TONE[tone].label} tracking-[0.2em] uppercase`}>{label}</p>
    </div>
    <p className="text-white/65 text-[14px] sm:text-[15px] leading-relaxed">{body}</p>
  </div>
);

const ReportLine = ({ label, value }) => (
  <div>
    <p className="text-[10px] font-outfit font-semibold text-white/40 tracking-[0.18em] uppercase mb-1">{label}</p>
    <p className="text-white/65 text-[13px] leading-relaxed">{value}</p>
  </div>
);

const CoreFeaturesSection = ({ heading = 'Core Features', subtitle, compact = false }) => {
  const [openId, setOpenId] = useState(null);
  const active = openId ? findFeatureById(openId) : null;

  const close = useCallback(() => setOpenId(null), []);
  const jump = useCallback((id) => {
    setOpenId(null);
    setTimeout(() => setOpenId(id), 220);
  }, []);

  useEffect(() => {
    if (!openId) return;
    const onKey = (e) => { if (e.key === 'Escape') close(); };
    document.body.style.overflow = 'hidden';
    window.addEventListener('keydown', onKey);
    return () => {
      document.body.style.overflow = '';
      window.removeEventListener('keydown', onKey);
    };
  }, [openId, close]);

  return (
    <section data-testid="core-features-section" className="relative py-24 overflow-hidden">
      <div className="absolute top-0 left-1/4 w-96 h-96 bg-violet-600/8 rounded-full blur-[120px] pointer-events-none" />
      <div className="absolute bottom-0 right-1/4 w-80 h-80 bg-cyan-500/8 rounded-full blur-[100px] pointer-events-none" />
      <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <FadeIn>
          <p className="text-xs font-semibold tracking-[0.2em] uppercase text-purple-400 mb-3 font-outfit">Feature Explorer</p>
          <h2 className="font-outfit font-bold text-3xl sm:text-4xl lg:text-5xl text-white tracking-tight mb-4 max-w-3xl">{heading}</h2>
          <p className="text-white/55 text-base leading-relaxed mb-12 max-w-2xl">
            {subtitle || 'Explore the systems that make ROMIND KIDS a safer creative universe for children aged 9–14. Hover to preview, tap any card to open a full deep dive.'}
          </p>
        </FadeIn>

        <div className={`grid gap-4 ${compact ? 'grid-cols-1 sm:grid-cols-2 lg:grid-cols-3' : 'grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-5'}`}>
          {coreFeatures.map((f, i) => (
            <FadeIn key={f.id} delay={Math.min(i * 50, 350)}>
              <FeatureCard feature={f} index={i} onOpen={setOpenId} />
            </FadeIn>
          ))}
        </div>

        <FadeIn delay={400}>
          <div className="mt-10 inline-flex items-center gap-2 px-4 py-2 rounded-full bg-white/[0.04] border border-white/10 text-[11px] text-white/45">
            <ChevronDown size={12} className="text-purple-400" />
            Click any card to open a full Feature Report and deep dive.
          </div>
        </FadeIn>
      </div>

      {active && <DeepDivePanel feature={active} onClose={close} onJump={jump} />}
    </section>
  );
};

export default CoreFeaturesSection;
